import React, { useState } from 'react';
import { Form, Input, Button, Table, message, Typography, Modal, Layout, Tag, Space } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { checkDomainAvailability, registerDomain, checkPaymentStatus } from '../../services/api';
import DomainPaymentForm from './DomainPaymentForm';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';

const { Title, Text } = Typography;

const DomainRegistration = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [payWithDubleu, setPayWithDubleu] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [domainSuggestions, setDomainSuggestions] = useState([]);
  const [searchedDomain, setSearchedDomain] = useState('');
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const handleCheck = async (values) => {
    setLoading(true);
    try {
      const result = await checkDomainAvailability(values.domain);
      setSearchedDomain(result.domain);
      setDomainSuggestions(result.suggestions);
    } catch (error) {
      console.error('Error checking domain availability:', error);
      message.error('Failed to check domain availability. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const mobileColumns = [
    {
      title: 'Domain Details',
      key: 'details',
      render: (record) => (
        <Space direction="vertical" size="small" className="w-full">
          <div className="font-medium">{record.domain}</div>
          <Space>
            {record.available ? 
              <Tag color="success" icon={<CheckCircleOutlined />}>Available</Tag> : 
              <Tag color="error" icon={<CloseCircleOutlined />}>Unavailable</Tag>
            }
          </Space>
          <div>
            {record.discount > 0 ? (
              <Space direction="vertical" size={0}>
                <Text delete className="text-sm">₹{record.original_price.toFixed(2)}</Text>
                <Space>
                  <Text type="success" strong>₹{record.final_price.toFixed(2)}</Text>
                  {record.offer_applied && (
                    <Tag color="blue">OFFER</Tag>
                  )}
                </Space>
              </Space>
            ) : (
              <Text>₹{record.original_price.toFixed(2)}</Text>
            )}
          </div>
          <Button 
            type="primary" 
            disabled={!record.available}
            onClick={() => handleRegisterClick(record)}
            block
          >
            {record.available ? 'Register' : 'Unavailable'}
          </Button>
          <Button 
            type="primary" 
            disabled={!record.available}
            onClick={() => handleRegisterClick(record,true)}
            block
          >
            {record.available ? 'Register' : 'Unavailable'}
          </Button>
        </Space>
      ),
    }
  ];

  const desktopColumns = [
    {
      title: 'Domain',
      dataIndex: 'domain',
      key: 'domain',
    },
    {
      title: 'Available',
      dataIndex: 'available',
      key: 'available',
      render: (available) => (
        available ? 
          <CheckCircleOutlined style={{ color: 'green' }} /> : 
          <CloseCircleOutlined style={{ color: 'red' }} />
      ),
    },
    {
      title: 'Price',
      key: 'price',
      render: (_, record) => (
        <div>
          {record.discount > 0 ? (
            <>
              <Text delete>₹{record.original_price.toFixed(2)}</Text>
              <br />
              <Text type="success">₹{record.final_price.toFixed(2)}</Text>
              {record.offer_applied && (
                <Tag color="blue" style={{ marginLeft: 8 }}>
                  OFFER
                </Tag>
              )}
            </>
          ) : (
            `₹${record.original_price.toFixed(2)}`
          )}
        </div>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button 
          type="primary" 
          disabled={!record.available}
          onClick={() => handleRegisterClick(record)}
        >
          {record.available ? 'Register' : 'Unavailable'}
        </Button>
      ),
    },
    {
      title: 'Register with Dubleu website builder',
      key: 'action2',
      render: (_, record) => (
        <>
        <Button 
          type="primary"
          style={(record.available) ? {background: 'black'} : {}}
          disabled={!record.available}
          onClick={() => handleRegisterClick(record,true)}
        >
          {record.available ? `Register @ ₹ ${record.final_domain_price_with_dubleu.toFixed(2)}` : 'Unavailable'}
        </Button>
        <div>
            {record.available ? `You pay ₹ ${record.final_domain_price_with_dubleu.toFixed(2)} for domain + ₹ ${record.final_dubleu_price_with_dubleu.toFixed(2)} for website` : ``}
        </div>
        </>
      ),
    },
  ];

  const handleRegisterClick = async (domainData,withDubleu=false) => {
    setSelectedDomain({...domainData, 'final_price': (withDubleu) ? domainData.final_price_with_dubleu : domainData.final_price});
    try {
      const the_price = (withDubleu) ? domainData.final_price_with_dubleu : domainData.final_price;
      setPayWithDubleu(withDubleu);
      await registerDomain(domainData.domain, the_price, withDubleu);
      message.success(`Domain ${domainData.domain} registered successfully!`);
      const updatedSuggestions = domainSuggestions.map(suggestion => 
        suggestion.domain === domainData.domain ? { ...suggestion, available: false } : suggestion
      );
      setDomainSuggestions(updatedSuggestions);
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('Insufficient balance')) {
        setIsModalVisible(true);
      } else {
        message.error('Failed to register domain. Please try again.');
      }
    }
  };

  const handlePaymentSuccess = async (paymentId) => {
    try {
      let attempts = 0;
      const maxAttempts = 50;
      const pollInterval = 2000;

      const checkPayment = async () => {
        if (attempts >= maxAttempts) {
          message.error(`Couldn't verify the payment yet, if done, we will register within few hours, else will refund.`);
          navigate('/domains');
          return;
        }

        const status = await checkPaymentStatus(paymentId);
        
        if (status.verified) {
          message.success(`Domain ${selectedDomain.domain} registered successfully!`);
          const updatedSuggestions = domainSuggestions.map(suggestion => 
            suggestion.domain === selectedDomain.domain ? { ...suggestion, available: false } : suggestion
          );
          setDomainSuggestions(updatedSuggestions);
          setIsModalVisible(false);
          setSelectedDomain(null);
          navigate('/domains');
        } else if (status.failed) {
          message.error(`Payment failed, if your account is deducted, it will be automatically be received in your bank account in 7 days.`);      
        } else {
          attempts++;
          setTimeout(checkPayment, pollInterval);
        }
      };

      await checkPayment();
    } catch (error) {
      console.error('Error in registration process:', error);
      message.error(`Failed to complete registration. If done, it will reflect in your dashboard within few hours, else will refund.`);
      navigate('/');
      setIsModalVisible(false);
      setSelectedDomain(null);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setSelectedDomain(null);
  };

  return (    
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      <Title level={isMobile ? 3 : 2}>Register a new domain</Title>
      
      <Form 
        form={form} 
        onFinish={handleCheck} 
        layout={isMobile ? "vertical" : "horizontal"} 
        className="mb-6"
      >
        <Form.Item
          name="domain"
          rules={[{ required: true, message: 'Please input a domain name!' }]}
          className={isMobile ? "mb-4 w-full" : "flex-2 mr-4"}
        >
          <Input 
            placeholder="Enter domain (e.g., example.com)" 
            size={isMobile ? "large" : "middle"}
          />
        </Form.Item>
        <Form.Item className={isMobile ? "w-full" : ""}>
          <Button 
            type="primary" 
            htmlType="submit" 
            loading={loading}
            icon={<SearchOutlined />}
            size={isMobile ? "large" : "middle"}
            block={isMobile}
          >
            Check Availability
          </Button>
        </Form.Item>
      </Form>

      {searchedDomain && (
        <Title level={isMobile ? 4 : 3} className="mb-4">
          Results for: {searchedDomain}
        </Title>
      )}

      {domainSuggestions.length > 0 && (
        <Table 
          columns={isMobile ? mobileColumns : desktopColumns} 
          dataSource={domainSuggestions} 
          rowKey="domain"
          pagination={false}
          className={isMobile ? "ant-table-mobile" : ""}
          scroll={{ x: isMobile ? undefined : true }}
        />
      )}
      
      <Modal
        title=""
        open={isModalVisible}
        onCancel={handleModalCancel}
        footer={null}
        width={isMobile ? "100%" : 600}
        style={isMobile ? { margin: 0 } : {}}
      >
        {selectedDomain && (
          <DomainPaymentForm
            domain={selectedDomain.domain}
            price={selectedDomain.final_price}
            withDubleu={payWithDubleu}
            onPaymentSuccess={handlePaymentSuccess}
            onCancel={handleModalCancel}
          />
        )}
      </Modal>
    </div>
  );
};

export default DomainRegistration;